import { FC, useEffect, PropsWithChildren } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import { useHookstate } from '@hookstate/core';
import NoSleep from 'nosleep.js';
import styled from 'styled-components';
import { isMobile } from 'react-device-detect';
import { Header } from './containers/Header';
import { BingoClassic } from './containers/BingoClassic';
import { SideGames } from './containers/SideGames';
import { BackgroundSounds } from './containers/BackroundSounds';
import { LottieEffect } from './containers/LottieEffect';
import { preloadLottieEffectOverlay } from './components/LottieEffectOverlay/lazy';
import { BuyModal } from './containers/BuyModal';
import { PickCardsModal } from './containers/PickCardsModal';
import { AutobuyModal } from './containers/AutobuyModal';
import { RecentWinnersModal } from './containers/RecentWinnersModal';
import { GameCancelledModal } from './containers/GameCancelledModal';
import { BuyingInvalidModal } from './containers/BuyingInvalidModal';
import { MessageModal } from './containers/MessageModal';
import { SettingsModal } from './containers/SettingsModal';
import { GameInfoModal } from './containers/GameInfoModal';
import { PartWinnerModalSG } from './containers/PartWinnerModalSG';
import { DauberStyleModal } from './containers/DauberStyleModal';
import { DauberColorModal } from './containers/DauberColorModal';
import { AliasModal } from './containers/AliasModal';
import { FpeTokenModal } from './containers/FpeTokenModal';
import { Loading } from './components/Loading';
import { useBackend, BackendContext } from './hooks/use-backend';
import { useChat, ChatContext } from './hooks/use-chat';
import { useBoughtTickets } from './hooks/use-bought-tickets';
import { chatMeta as chatMetaState } from './stores/chat';
import {
    alias as aliasState,
    current as currentState,
    next as nextState,
    screenConfig as screenConfigState,
    meta as metaState,
    checkPlayerAlias,
    getScreenConfig,
} from './stores/backend';
import {
    useSoundInitialization,
    SoundContext,
} from './hooks/use-sound-handler';
import GlobalStyle from './globalStyle';
import { getQuery } from './lib/query';
import { BodyBackgroundLottie } from './components/BodyBackgroundLottie';
import { suspension } from './lib/suspension';

const noSleep = new NoSleep();

const PageNotFound = () => {
    return <h1>Page Not Found!</h1>;
};

const BackendContextProvider: FC<PropsWithChildren> = (props) => {
    const backendPayload = useBackend();

    if (!backendPayload) {
        return null;
    }

    return <BackendContext.Provider {...props} value={{ backendPayload }} />;
};

const ChatContextProvider: FC<PropsWithChildren> = (props) => {
    const chatPayload = useChat();

    if (!chatPayload) {
        return null;
    }

    return <ChatContext.Provider {...props} value={{ chatPayload }} />;
};

export const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    position: relative;
    max-width: ${isMobile ? '46.875rem' : '100rem'};
    height: 100%;
    margin: 0 auto;
`;

const AppLayout: FC = () => {
    const suspended = !!useHookstate(suspension).value;
    const { screenConfig, language } = useHookstate(screenConfigState);
    const alias = useHookstate(aliasState);
    const current = useHookstate(currentState);
    const meta = useHookstate(metaState);
    const next = useHookstate(nextState);
    const chatMeta = useHookstate(chatMetaState);
    const { pathname } = useLocation();
    const bought = useBoughtTickets(current);

    const sounds = useSoundInitialization({
        loaded: meta.loaded.screenConfig.value,
        sound: meta.sound.value,
        language: language.value,
        caller: screenConfig.room.caller.value.toLowerCase(),
        announcer: screenConfig.room.announcer.value.toLowerCase(),
    });

    const { CSSOVERRIDEURL, SESSIONID } = getQuery();

    useEffect(() => {
        getScreenConfig();
    }, []);

    useEffect(() => {
        const enableNoSleep = () => {
            noSleep.enable();
            document.removeEventListener('touchstart', enableNoSleep, false);
        };

        document.addEventListener('touchstart', enableNoSleep, false);
    }, []);

    useEffect(() => {
        if (CSSOVERRIDEURL) {
            const link = document.createElement('link');

            link.setAttribute('rel', 'stylesheet');
            link.setAttribute('href', CSSOVERRIDEURL);
            link.setAttribute('crossorigin', 'anonymous');

            document.head.appendChild(link);
        }
    }, [CSSOVERRIDEURL]);

    useEffect(() => {
        if (!meta.loaded.screenConfig.value) {
            return;
        }

        checkPlayerAlias(SESSIONID);
    }, [SESSIONID, meta.loaded.screenConfig.value]);

    const hasScreenConfigError =
        meta.modals.msgCommon.isOpen.value &&
        (meta.modals.msgCommon.name.value === 'error.not.authenticated' ||
            meta.modals.msgCommon.name.value === 'error.general');

    const showAliasModal =
        !hasScreenConfigError &&
        meta.loaded.screenConfig.value &&
        !alias.done.value &&
        alias.required.value;
    const showLoading =
        !hasScreenConfigError &&
        !showAliasModal &&
        (!current.game_sid.value ||
            !meta.loaded.screenConfig.value ||
            (current.game_mode.value === 'GAME_MODE' &&
                bought === 0 &&
                !next.game_sid.value));

    useEffect(() => {
        if (showLoading) {
            preloadLottieEffectOverlay();
        }
    }, [showLoading]);

    const rootRouteElement = showLoading ? <Loading /> : <BingoClassic />;

    let content: JSX.Element | null = (
        <BackendContextProvider>
            <ChatContextProvider>
                {isMobile &&
                chatMeta.chatToggle.value &&
                pathname !== '/side' ? null : (
                    <Header />
                )}
                <Routes>
                    <Route path="/" element={rootRouteElement} />
                    <Route path="/side" element={<SideGames />} />
                    <Route path="/*" element={<PageNotFound />} />
                </Routes>
                <BackgroundSounds />
                <LottieEffect />
                <BuyModal />
                <PickCardsModal />
                <AutobuyModal />
                <GameCancelledModal />
                <BuyingInvalidModal />
                <SettingsModal />
                <GameInfoModal />
                <PartWinnerModalSG />
                <RecentWinnersModal />
                <DauberStyleModal />
                <DauberColorModal />
                <FpeTokenModal />
            </ChatContextProvider>
        </BackendContextProvider>
    );

    if (suspended || hasScreenConfigError) {
        content = null;
    } else if (showAliasModal) {
        content = <AliasModal />;
    }

    return (
        <SoundContext.Provider value={sounds}>
            <GlobalStyle />
            {!suspended && <BodyBackgroundLottie />}
            <Wrapper>
                {content}
                <MessageModal />
            </Wrapper>
        </SoundContext.Provider>
    );
};

export default AppLayout;
